import { ExternalLink, Home, UsersRound } from "lucide-react";
import { InterestPhoneForm } from "../components/InterestPhoneForm";
import { Button } from "../components/ui/button";
import { contact, stays } from "../data/siteData";
import { useSEO } from "../hooks/useSEO";

export default function Accommodation() {
  useSEO({
    title: "Accommodation — Stays Curated for Surfers",
    description: "Stay close to the tide. A luxury high-rise apartment, Eva's warm homestay, and a surf hostel coming soon — all curated by surfers for surfers in Kannur.",
    path: "/accommodation",
  });

  return (
    <main data-testid="accommodation-page" className="bg-[#0A0F1C] px-5 pb-24 pt-32 text-white md:px-10 md:pb-32">
      <section data-testid="accommodation-hero-section" className="mx-auto max-w-7xl">
        <p data-testid="accommodation-hero-kicker" className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF5E3A]">Accommodation</p>
        <div className="mt-6 grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-end">
          <h1 data-testid="accommodation-hero-title" className="font-heading text-5xl font-black leading-none tracking-tighter sm:text-6xl">Stay close to the tide.</h1>
          <p data-testid="accommodation-hero-description" className="max-w-2xl text-base leading-8 text-slate-400 md:text-lg">
            Curated by surfers, for surfers — choose a polished high-rise stay, a warm homestay with home-cooked breakfast, or register interest for the surf hostel coming soon.
          </p>
        </div>
      </section>

      <section data-testid="accommodation-stays-section" className="mx-auto mt-16 grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
        <aside data-testid="accommodation-sticky-panel" className="h-fit rounded-[2rem] border border-white/10 bg-[#1C1917]/50 p-8 backdrop-blur-xl lg:sticky lg:top-28">
          <Home className="h-10 w-10 text-[#FFC059]" />
          <h2 data-testid="accommodation-sticky-title" className="mt-6 font-heading text-4xl font-bold tracking-tight text-white">Stay your way in Kannur.</h2>
          <p data-testid="accommodation-sticky-copy" className="mt-5 text-base leading-8 text-slate-400">
            Each stay route is selected around surf days: easy mornings, comfortable recovery, and quick access to club guidance when you need it.
          </p>
          <div data-testid="accommodation-booking-note" className="mt-8 border-l-2 border-[#FF5E3A] pl-5 text-sm leading-7 text-slate-300">
            Built around the way surfers travel: simple logistics, good rest, and a real connection to Kannur’s coast.
          </div>
        </aside>

        <div className="grid gap-8">
          {stays.map((stay, index) => (
            <article data-testid={`stay-card-${stay.name.toLowerCase().replaceAll(" ", "-")}`} key={stay.name} className="overflow-hidden rounded-[2rem] border border-white/10 bg-[#1C1917]/50 backdrop-blur-xl transition-all duration-300 hover:-translate-y-2 hover:border-white/20 hover:bg-[#1C1917]/70">
              <img data-testid={`stay-image-${index}`} src={stay.image} alt={`${stay.name} — ${stay.style}`} loading="lazy" decoding="async" className="aspect-[16/9] w-full object-cover object-center" />
              <div className="p-7 md:p-9">
                <p data-testid={`stay-style-${index}`} className="text-xs font-bold uppercase tracking-[0.28em] text-[#FFC059]">{stay.style}</p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 data-testid={`stay-title-${index}`} className="font-heading text-3xl font-bold text-white">{stay.name}</h3>
                  {stay.comingSoon && <span data-testid={`stay-coming-soon-${index}`} className="w-fit rounded-full border border-[#FFC059]/40 bg-[#FFC059]/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-[#FFC059]">Coming Soon</span>}
                </div>
                {stay.price && <p data-testid={`stay-price-${index}`} className="mt-4 font-heading text-2xl font-black text-[#FFC059]">{stay.price}</p>}
                <p data-testid={`stay-copy-${index}`} className="mt-4 text-base leading-8 text-slate-400">{stay.copy}</p>
                {stay.comingSoon ? (
                  <div data-testid={`stay-interest-panel-${index}`} className="mt-7 p-0">
                    <p data-testid={`stay-interest-copy-${index}`} className="text-sm leading-7 text-slate-300">Want a bed when the surf hostel opens? Register interest with just your phone number.</p>
                    <InterestPhoneForm interest={stay.name} source="accommodation-page" testId={`stay-interest-${index}`} />
                  </div>
                ) : stay.bookingType === "airbnb" ? (
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                    <Button data-testid={`stay-airbnb-button-${index}`} asChild variant="outline" className="rounded-xl border-white/20 bg-transparent font-semibold uppercase tracking-[0.16em] text-white hover:border-[#FF5E3A] hover:bg-transparent hover:text-[#FF5E3A]">
                      <a href={stay.bookingUrl} target="_blank" rel="noreferrer">Airbnb <ExternalLink className="h-4 w-4" /></a>
                    </Button>
                  </div>
                ) : (
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                    <Button data-testid={`stay-direct-button-${index}`} asChild className="rounded-xl bg-[#FF5E3A] font-semibold uppercase tracking-[0.16em] text-white hover:bg-[#FF7B5C]">
                      <a href={`tel:${contact.managerPhoneRaw}`}>Direct Booking</a>
                    </Button>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section data-testid="accommodation-group-section" className="mx-auto mt-16 max-w-7xl rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,rgba(13,148,136,0.18),rgba(255,94,58,0.14))] p-8 backdrop-blur-xl md:p-12">
        <UsersRound className="h-9 w-9 text-[#FFC059]" />
        <h2 data-testid="accommodation-group-title" className="mt-5 font-heading text-3xl font-bold text-white sm:text-4xl">Coming as a crew?</h2>
        <p data-testid="accommodation-group-copy" className="mt-4 max-w-3xl text-base leading-8 text-slate-300">For stay questions, message the Club Manager first on WhatsApp ({contact.managerPhone}) or call the Activity Operator on {contact.ownerPhone}.</p>
      </section>
    </main>
  );
}
